import type { Insight } from "../../hooks/useProgressReport";

export interface InsightsSectionProps {
  insights: Insight[];
}

// Background colors for different insight types
const insightStyles: Record<
  Insight["type"],
  { bgClass: string; borderClass: string; textClass: string }
> = {
  strength: {
    bgClass: "bg-gradient-to-r from-meadow-50 to-meadow-100/50",
    borderClass: "border-meadow-200",
    textClass: "text-meadow-800",
  },
  improvement: {
    bgClass: "bg-gradient-to-r from-sky-50 to-sky-100/50",
    borderClass: "border-sky-200",
    textClass: "text-sky-800",
  },
  celebration: {
    bgClass: "bg-gradient-to-r from-sunshine-50 to-fairy-50",
    borderClass: "border-sunshine-200",
    textClass: "text-sunshine-800",
  },
  tip: {
    bgClass: "bg-gradient-to-r from-magic-50 to-magic-100/50",
    borderClass: "border-magic-200",
    textClass: "text-magic-800",
  },
};

/**
 * Insights section showing personalized learning highlights.
 * Features colorful cards with encouraging messages.
 */
export function InsightsSection({ insights }: InsightsSectionProps) {
  if (insights.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <h2 className="font-display font-bold text-display-sm text-neutral-800 flex items-center gap-2">
        <span className="text-xl">💡</span>
        Highlights
      </h2>

      <div className="space-y-2">
        {insights.map((insight, index) => {
          const style = insightStyles[insight.type];

          return (
            <div
              key={index}
              className={`rounded-xl ${style.bgClass} border ${style.borderClass} p-4 transition-all hover:shadow-sm print:shadow-none`}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl flex-shrink-0" role="img" aria-hidden="true">
                  {insight.icon}
                </span>
                <p className={`font-body font-medium ${style.textClass}`}>
                  {insight.message}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default InsightsSection;
